import { useStore } from "~/store";
import { useSchemaOrg } from "./useSchemaOrg";
import { useSeoUrls, ogLocale } from "./useSeoUrls";

/** Datos propios de una página de detalle (un servicio, un artículo) que sustituyen a los de su sección. */
export interface SeoPagina {
  /** Título completo de la página (con el nombre de la notaría). */
  title?: string
  description?: string
  /** URL absoluta de la imagen para compartir. */
  image?: string
  type?: 'website' | 'article'
  /** Fechas en formato AAAA-MM-DD. */
  publishedTime?: string
  modifiedTime?: string
  author?: string
  /** Nombre corto para las migas de pan. */
  nombre?: string
  /** Datos estructurados específicos de la página. */
  schema?: 'Service' | 'BlogPosting'
  /** No indexar la página (por ejemplo, una traducción a medias). */
  noindex?: boolean
  /** Nombre de la sección en las migas de pan, si no es el del panel. */
  seccion?: string
  /** Preguntas y respuestas visibles en la página, para los datos estructurados. */
  faqs?: { p: string; r: string }[]
  /** Ruta de la página en cada idioma (el slug se traduce con el contenido), para los hreflang. */
  rutasPorIdioma?: Record<string, string>
}

export function useMetaTags(notaria:any, PageName:string, SubPage?:string, pagina?: SeoPagina) {
  const store = useStore()
  const seo = useSeoUrls()
  
  const PageNames: any = {
    Equipo: { propertyName: "Team", displayName: "Equipo" },
    index: { propertyName: "Home", displayName: "Inicio" },
    Home: { propertyName: "Home", displayName: "Inicio" },
    Servicios: { propertyName: "Servicios", displayName: "Servicios" },
    Blog: { propertyName: "Blog", displayName: "Blog" },
    Contacto: { propertyName: "Contacto", displayName: "Contacto" },
    Notarios: { propertyName: "Notarios", displayName: "Notarios" },
    Tramites: { propertyName: "Tramites", displayName: "Trámites" },
    Testimonios: { propertyName: "Testimonios", displayName: "Testimonios" },
    "encargo-online": { propertyName: "TramiteOnline", displayName: "Encargo online" },
    "mi-encargo": { propertyName: "TramiteOnline", displayName: "Mi encargo" },
    "encargo-qr": { propertyName: "TramiteOnline", displayName: "Encargo online" },
    "tramite-online": { propertyName: "TramiteOnline", displayName: "Encargo online" },
    "mi-tramite": { propertyName: "TramiteOnline", displayName: "Mi encargo" },
    "tramite-qr": { propertyName: "TramiteOnline", displayName: "Encargo online" }
  }

  let ThemePropertyName: string = PageNames[PageName] ? PageNames[PageName].propertyName : PageName
  let DisplayPageName: string = PageNames[PageName] ? PageNames[PageName].displayName : PageName
  const pageTheme = notaria.Theme[ThemePropertyName]

  // Una página de detalle (servicio, artículo) usa lo suyo; si no, lo de su sección en el panel
  const title = pagina?.title || pageTheme?.Titulo?.Texto || DisplayPageName
  const description = pagina?.description || pageTheme?.Description || ""
  const keywords = pageTheme?.Keywords || ""
  const esArticulo = pagina?.type === 'article'

  // Imagen para redes sociales (Prioridad: la de la página > Banner > Logo > Default)
  const shareImageId = notaria.Theme?.Home?.Banner?.Imagen || notaria.Theme?.Home?.Banner?.ImagenLogo || ""
  const shareImage = pagina?.image || (shareImageId ? `https://${store.host}/appdata/static/${shareImageId}` : `https://${store.host}/favicon.ico`)

  // URL canónica: la de la propia página en el idioma actual (con prefijo si no es el principal)
  const shareUrl = seo.url(seo.idioma)
  // El <title> lleva el nombre de la notaría por el titleTemplate de app.vue; las redes no lo aplican
  const ogTitle = `${title} | ${notaria.Nombre}`

  // 1. Prioridad: Etiquetas para SEO (Lo que ve Google y Redes Sociales)
  useSeoMeta({
    title: title,
    description: description,
    keywords: keywords,
    // Open Graph
    ogTitle: ogTitle,
    ogDescription: description,
    ogImage: shareImage,
    ogUrl: shareUrl,
    ogType: esArticulo ? 'article' : 'website',
    ogSiteName: notaria.Nombre,
    ogLocale: ogLocale(seo.idioma),
    ogLocaleAlternate: seo.idiomas.filter((l) => l !== seo.idioma).map(ogLocale),
    ...(esArticulo ? {
      articlePublishedTime: pagina?.publishedTime,
      articleModifiedTime: pagina?.modifiedTime || pagina?.publishedTime,
      articleAuthor: pagina?.author ? [pagina.author] : undefined
    } : {}),
    // Twitter
    twitterCard: 'summary_large_image',
    twitterTitle: ogTitle,
    twitterDescription: description,
    twitterImage: shareImage,
    // Otros: vista previa completa en los resultados de Google y en las respuestas con IA
    robots: pagina?.noindex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    googleSiteVerification: notaria.Personalizar?.GoogleSiteVerification || undefined
  })

  // Idiomas alternativos: en un servicio o artículo cada idioma tiene su slug, así que solo se declaran los que se conocen
  const idiomasConRuta = Object.keys(pagina?.rutasPorIdioma ?? {})
  const alternativas = !pagina?.nombre
    ? seo.alternativas
    : idiomasConRuta.length > 1
      ? [...idiomasConRuta.map((l) => ({ hreflang: l, href: seo.url(l, pagina!.rutasPorIdioma![l]) })),
         { hreflang: 'x-default', href: seo.url(seo.principal, pagina!.rutasPorIdioma![seo.principal] ?? seo.ruta) }]
      : []

  // 2. Etiquetas de cabecera adicionales (Atributos HTML, Canonical, idiomas alternativos)
  useHead({
    title: title,
    htmlAttrs: {
      lang: notaria.Languaje || seo.idioma || 'es'
    },
    meta: notaria.Personalizar?.BingSiteVerification ? [{ name: 'msvalidate.01', content: notaria.Personalizar.BingSiteVerification }] : [],
    link: [
      { rel: 'canonical', href: shareUrl },
      ...alternativas.map((a) => ({ rel: 'alternate', hreflang: a.hreflang, href: a.href }))
    ]
  })

  // 3. Datos Estructurados (Schema.org)
  const esPortada = seo.ruta === '/'
  const migas: { name: string; url: string }[] = [{ name: notaria.Nombre, url: seo.url(seo.idioma, '/') }]
  if (!esPortada) {
    const seccion = '/' + seo.ruta.split('/')[1]
    migas.push({ name: pagina?.seccion || pageTheme?.Titulo?.Texto || DisplayPageName, url: seo.url(seo.idioma, pagina?.nombre ? seccion : seo.ruta) })
    if (pagina?.nombre) migas.push({ name: pagina.nombre, url: shareUrl })
  }
  useSchemaOrg(notaria, {
    url: shareUrl,
    esPortada,
    migas,
    tipo: pagina?.schema,
    titulo: pagina?.nombre || title,
    descripcion: description,
    imagen: pagina?.image,
    publicado: pagina?.publishedTime,
    modificado: pagina?.modifiedTime,
    autor: pagina?.author,
    faqs: pagina?.faqs
  })
}
